import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Button, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { PlusIcon } from '@navikt/aksel-icons';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { useGetCustomSurveyTemplatesQuery } from '../../hooks/queries/useGetCustomSurveyTemplatesQuery';
import { useGetCustomSurveysByTeamIdQuery } from '../../hooks/queries/useGetCustomSurveysByTeamIdQuery';
import { Routes } from '../../enums/Routes';
import { LiveList } from './components/LiveList';
import { HistoryList } from './components/HistoryList';
import { historyItems, latestSurvey, liveItems, TemplateItem } from './customSurveysView';
import { CustomSurveyDashboardPageUtils } from './CustomSurveyDashboardPage.utils';
import classes from './CustomSurveyDashboardPage.module.css';

export const CustomSurveyDashboardPage = () => {
  const { t } = useTranslation();
  const { selectedTeamId } = useSelectedUnitContext();

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useGetCustomSurveyTemplatesQuery({
    teamId: selectedTeamId,
    filterByUsage: true, // Bare maler teamet har brukt
  });

  const { data: surveys, isLoading: isLoadingSurveys, error: surveysError } =
    useGetCustomSurveysByTeamIdQuery(selectedTeamId);

  const isLoading = isLoadingTemplates || isLoadingSurveys;
  const error = templatesError || surveysError;

  const content = () => {
    if (CustomSurveyDashboardPageUtils.shouldShowTeamSelectionPrompt(selectedTeamId)) {
      return <Paragraph className={classes.subtle}>{t('customSurveys.dashboard.teamPrompt')}</Paragraph>;
    }

    if (isLoading) {
      return (
        <div aria-busy="true" className={classes.loading}>
          <span className="ds-sr-only">{t('customSurveys.dashboard.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={200} />
          <Skeleton variant="rectangle" width="100%" height={280} />
        </div>
      );
    }

    if (error) {
      return (
        <Alert data-color="danger">
          <Heading level={3} data-size="2xs">
            {t('customSurveys.dashboard.error.title')}
          </Heading>
          <Paragraph>{t('customSurveys.dashboard.error.message')}</Paragraph>
        </Alert>
      );
    }

    if (CustomSurveyDashboardPageUtils.shouldShowEmptyState(templates)) {
      return (
        <section aria-labelledby="custom-surveys-empty" className={classes.empty}>
          <Heading level={3} data-size="xs" id="custom-surveys-empty">
            {t('customSurveys.dashboard.emptyState.title')}
          </Heading>
          <Paragraph className={classes.subtle}>{t('customSurveys.dashboard.emptyState.description')}</Paragraph>
        </section>
      );
    }

    const items: TemplateItem[] = (templates ?? []).map((template) => ({
      template,
      latest: surveys ? latestSurvey(template.id, surveys) : undefined,
    }));
    const live = liveItems(items);

    return (
      <>
        {live.length > 0 && <LiveList items={live} />}
        <HistoryList items={historyItems(templates ?? [], surveys ?? [])} />
      </>
    );
  };

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <div className={classes.intro}>
          <Heading level={2} data-size="md">
            {t('customSurveys.dashboard.sectionTitle')}
          </Heading>
          <Paragraph className={classes.subtle}>{t('customSurveys.dashboard.sectionDescription')}</Paragraph>
        </div>
        <Button asChild>
          <Link to={Routes.CreateSurvey}>
            <PlusIcon aria-hidden />
            {t('customSurveys.dashboard.create')}
          </Link>
        </Button>
      </div>
      {content()}
    </div>
  );
};
